// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate, useParams } from 'react-router-dom';

import {
  fetchInfos,
  selectIsFetchingInfos,
  selectIsLoadedInfos,
} from '@gandi/billing';
import {
  selectHasDomainCorporateProducts,
  setCartOwner,
  selectCartProductOwner,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import type { Organization } from '@gandi/redux-organization';
import { fetchOneOrganization, setCurrentId } from '@gandi/redux-sharing';
import { localeSelector, useTranslator } from '@gandi/translate';

import type { OwnerType } from 'src/Cart/AccountChoice/components/ResellerOwnerType';
import {
  updateCartProductOwner,
  updateCartConfigurationForOwnership,
} from 'src/Cart/AccountChoice/modules/actions';
import type { CartOwnerCandidate } from 'src/Cart/AccountChoice/types';
import { getSetupOverviewUrl } from 'src/Cart/Setup/modules/helpers';
import { fetchOneCorpoPack } from 'src/Cart/modules/corporate';
import { navigateTo } from 'src/helpers/navigation';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useOrganization } from 'src/hooks/useOrganization';
import { applyTemplateIfNeeded } from 'src/modules/app/actions';
import { selectShopURL } from 'src/modules/config';
import { selectShopCurrent } from 'src/modules/organization/selectors';

import { useProductOwnerChoice } from '../../hooks/useProductOwnerChoice';

export type CartSummaryContextType = {
  frozen: boolean,
  isReseller: boolean,
  orderId: number | null,
  // TODO: handle owner confusion between cartOwner and productOwner
  owner: null | CartOwnerCandidate,
  cartOwnerOrganization: ?Organization,
  // TODO: handle owner confusion between cartOwner and productOwner
  // as OwnerType is comming from ResellerOwner context
  // it is certainly a productOwner
  // remane to productOwnerType?
  ownerType: OwnerType | null,
  productOwnerOrganization: ??Organization,
  flushPrepaid: boolean,
  setFlushPrepaid: (newValue: boolean) => void,
  setFrozen: (newValue: boolean) => void,
  setOrderId: (newValue: number) => void,
  // TODO: handle owner confusion between cartOwner and productOwner
  // as it can be CartOwnerCandidate it seems to be cartOwner
  // rename to setCartOwner?
  setOwner: (newValue: null | CartOwnerCandidate) => void,
  // TODO: handle owner confusion between cartOwner and productOwner
  // it is aligned with the props `ownerType`
  // rename to setProductOwnerType?
  setOwnerType: (newValue: OwnerType | null) => void,
  setSubmitting: (newValue: boolean) => void,
  submitting: boolean,
  handleConfigurationClick: (evt: SyntheticEvent<>) => Promise<void>,
  updateSharingIdContext: ({
    newSharingId: string,
    guess?: boolean,
    route?: string,
    reload?: boolean,
  }) => Promise<>,
  sameOwnerCheckboxIsChecked: boolean,
  setSameOwnerCheckboxIsChecked: React.ReactSetStateFunction<boolean>,
};

const CartSummaryContext: React.Context<CartSummaryContextType | void> =
  React.createContext(undefined);

export const useCartSummaryContext = (): CartSummaryContextType => {
  const context = React.useContext(CartSummaryContext);
  if (context === undefined) {
    throw new Error(
      'useCartSummaryContext should be use inside <CartSummaryProvider>',
    );
  }
  return context;
};

type Props = {
  children: React.MixedElement,
};

export const CartSummaryProvider = ({
  children,
}: Props): React.MixedElement => {
  const dispatch = useDispatch();
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const { __ } = useTranslator();

  const [owner, setOwner] = React.useState<null | CartOwnerCandidate>(null);
  const [frozen, setFrozen] = React.useState<boolean>(true);
  const [orderId, setOrderId] = React.useState<number | null>(null);
  const [ownerType, setOwnerType] = React.useState<OwnerType | null>(null);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  // Used in the checkout page
  const [flushPrepaid, setFlushPrepaid] = React.useState<boolean>(false);

  const currentOrg = useAppSelector(state => selectShopCurrent(state, true));
  const sharingId = currentOrg?.id;

  const billingProps = { sharingId };
  const { profile: cartOwnerOrganization } = useOrganization(sharingId);
  const isReseller = !!cartOwnerOrganization?.reseller;

  const productOwnerId = useAppSelector(selectCartProductOwner);
  const { profile: productOwnerOrganization } = useOrganization(productOwnerId);

  const fetchingBillingInfo = useAppSelector(state =>
    selectIsFetchingInfos(state, billingProps),
  );

  const loadedBillingInfo = useAppSelector(state =>
    selectIsLoadedInfos(state, billingProps),
  );

  const corporateProduct = useAppSelector(selectHasDomainCorporateProducts);

  const locale = useAppSelector(localeSelector);
  const shop = useAppSelector(selectShopURL);

  const { checked, setChecked } = useProductOwnerChoice({ isReseller });

  const updateSharingIdContext: (opts: {
    newSharingId: string,
    guess?: boolean,
    route?: string,
    reload?: boolean,
  }) => Promise<void> = React.useCallback(
    async ({
      newSharingId,
      guess = false,
      route: route_ = '',
      reload = false,
    }) => {
      setFrozen(true);
      // FIXME: once sufficiently validated, propagate this change
      // to other cases of redirectToSharingIdContext.
      try {
        if (reload) {
          throw new Error('Perform a hard-reload.');
        }
        // Fetch the current org's details.
        const response = await dispatch(fetchOneOrganization(newSharingId));
        // $FlowFixMe[incompatible-call]
        setOwner(response.data);
        // Override the current id in the redux state.
        dispatch(setCurrentId(newSharingId));

        // This will be reverted to the inital sharing id
        // if user leaves this page, as a mitigation of the sharing_id in the URL
        // dictating the context.
        // See `Configure`'s `componenWillUnmount` lifecycle method.

        // Alway fetch the current org's prepaid info
        // Necessary to:
        // - ensure current cart owner has a currency set
        // - check owner's grid against bundle grid limitations if any.

        const promises = [];
        if (!fetchingBillingInfo && !loadedBillingInfo) {
          promises.push(dispatch(fetchInfos()));
        }

        if (currentOrg.corporate && corporateProduct) {
          promises.push(dispatch(fetchOneCorpoPack(newSharingId)));
        }

        await Promise.allSettled(promises);

        setFrozen(false);
        const route = route_;
        if (route) {
          navigate(route);
        }
      } catch (err) {
        // Pass and do hard-reload redirect.
        const route = route_ || `/cart/${cartId}/configure/account-choice`;
        let redirect = `${shop}/${locale}/${newSharingId}${route}`;
        if (guess) {
          redirect = `${redirect}?guess`;
        }
        navigateTo(redirect);
      }
    },
    [
      cartId,
      corporateProduct,
      currentOrg?.corporate,
      dispatch,
      fetchingBillingInfo,
      loadedBillingInfo,
      locale,
      navigate,
      setFrozen,
      shop,
    ],
  );

  const handleConfigurationClick: (evt: SyntheticEvent<>) => Promise<void> =
    React.useCallback(
      async (evt: SyntheticEvent<>) => {
        evt.preventDefault();

        setFrozen(true);
        setSubmitting(true);

        try {
          await dispatch(applyTemplateIfNeeded());
          await dispatch(updateCartProductOwner());
          await dispatch(setCartOwner({}));
          await dispatch(updateCartConfigurationForOwnership());

          // Perform a hard-reload only if the cart owner / sharing_id context
          // has been updated.
          const reload = !global.window.location.href.includes(sharingId);
          await updateSharingIdContext({
            newSharingId: sharingId,
            guess: false,
            route: getSetupOverviewUrl(cartId),
            reload,
          });
        } catch (err) {
          setFrozen(false);
          setSubmitting(false);
          dispatch(notifyDanger(__('Unexpected error occurred')));
        }
      },
      [__, cartId, dispatch, sharingId, updateSharingIdContext],
    );

  const handleSetFrozen = React.useCallback(
    (newValue: boolean) => setFrozen(newValue),
    [],
  );

  const handleSetOrderId = React.useCallback(
    (newValue: number) =>
      setOrderId(newValue ? parseInt(newValue, 10) : newValue),
    [],
  );

  const handleSetOwnerType = React.useCallback(
    (newValue: OwnerType | null) => setOwnerType(newValue),
    [],
  );
  const handleSetSubmitting = React.useCallback(
    (newValue: boolean) => setSubmitting(newValue),
    [],
  );

  const handleSetOwner = React.useCallback(
    (newValue: null | CartOwnerCandidate) => setOwner(newValue),
    [],
  );

  const handleSetFlushPrepaid = React.useCallback(
    (newValue: boolean) => setFlushPrepaid(newValue),
    [],
  );

  // TODO: Check if the useMemo is effectively preventing unnecessary re-renders
  // opposite/couple with some useCallback
  const contextValue = React.useMemo(
    () => ({
      cartOwnerOrganization,
      flushPrepaid,
      frozen,
      handleConfigurationClick,
      isReseller,
      orderId,
      owner,
      ownerType,
      productOwnerOrganization,
      sameOwnerCheckboxIsChecked: checked,
      setFlushPrepaid: handleSetFlushPrepaid,
      setFrozen: handleSetFrozen,
      setOrderId: handleSetOrderId,
      setOwner: handleSetOwner,
      setOwnerType: handleSetOwnerType,
      setSameOwnerCheckboxIsChecked: setChecked,
      setSubmitting: handleSetSubmitting,
      submitting,
      updateSharingIdContext,
    }),
    [
      cartOwnerOrganization,
      checked,
      flushPrepaid,
      frozen,
      handleConfigurationClick,
      handleSetFlushPrepaid,
      handleSetFrozen,
      handleSetOrderId,
      handleSetOwner,
      handleSetOwnerType,
      handleSetSubmitting,
      isReseller,
      orderId,
      owner,
      ownerType,
      productOwnerOrganization,
      setChecked,
      submitting,
      updateSharingIdContext,
    ],
  );

  return (
    <CartSummaryContext.Provider value={contextValue}>
      {children}
    </CartSummaryContext.Provider>
  );
};
